import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Briefcase,
  MapPin,
  Clock,
  DollarSign,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";

export const API_URL =
  process.env.REACT_APP_API_URL || "https://sbg-backend.onrender.com";

const ApplicationForm = () => {
  const { jobId } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [job, setJob] = useState(null);
  const [jobLoading, setJobLoading] = useState(true);
  const [jobError, setJobError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    resume: null,
    coverLetter: "",
    education: [
      { school: "", degree: "", fieldOfStudy: "", graduationYear: "" },
    ],
    workExperience: [
      {
        company: "",
        position: "",
        startDate: "",
        endDate: "",
        responsibilities: "",
      },
    ],
    skills: [""],
  });

  useEffect(() => {
    const fetchJobDetails = async () => {
      if (!jobId) {
        console.log('No jobId provided');
        setJobLoading(false);
        return;
      }
      console.log('Fetching job details for ID:', jobId);
      try {
        setJobLoading(true);
        const response = await axios.get(`${API_URL}/jobs/${jobId}`);
        console.log('Job details response:', response.data);
        setJob(response.data);
      } catch (error) {
        console.error('Error fetching job details:', error);
        if (error.response) {
          console.error('Error response:', error.response.data);
        }
        setJobError("Failed to load job details. Please try again later.");
      } finally {
        setJobLoading(false);
      }
    };

    fetchJobDetails();
  }, [jobId]);

  const handleInputChange = (e, index, section) => {
    const { name, value, type, files } = e.target;
    if (section) {
      if (section === "skills") {
        const updatedSkills = [...formData.skills];
        updatedSkills[index] = value;
        setFormData({ ...formData, skills: updatedSkills });
      } else {
        const updatedSection = [...formData[section]];
        updatedSection[index] = { ...updatedSection[index], [name]: value };
        setFormData({ ...formData, [section]: updatedSection });
      }
    } else if (type === "file") {
      setFormData((prevData) => ({
        ...prevData,
        [name]: files[0] || null,
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleAddItem = (section) => {
    setFormData({
      ...formData,
      [section]: [
        ...formData[section],
        section === "education"
          ? { school: "", degree: "", fieldOfStudy: "", graduationYear: "" }
          : section === "workExperience"
          ? {
              company: "",
              position: "",
              startDate: "",
              endDate: "",
              responsibilities: "",
            }
          : "",
      ],
    });
  };

  const handleRemoveItem = (section, index) => {
    const updatedSection = [...formData[section]];
    updatedSection.splice(index, 1);
    setFormData({ ...formData, [section]: updatedSection });
  };

  const validateStep = (step) => {
    const errors = {};
    // Add validation logic here
    return errors;
  };

  const handleNextStep = () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length === 0) {
      setCurrentStep(currentStep + 1);
      setFieldErrors({});
    } else {
      setFieldErrors(errors);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
    setFieldErrors({});
  };

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Validate all fields before submission
      const errors = validateAllFields();
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        throw new Error("Please fill in all required fields correctly.");
      }

      // Prepare form data
      const applicationData = new FormData();
      for (const [key, value] of Object.entries(formData)) {
        if (key === "resume") {
          applicationData.append("resume", value);
        } else if (Array.isArray(value)) {
          applicationData.append(key, JSON.stringify(value));
        } else {
          applicationData.append(key, value);
        }
      }
      applicationData.append("jobId", jobId);

      // Submit the application
      const response = await axios.post(
        `${API_URL}/applications`,
        applicationData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 201) {
        setShowSuccessAlert(true);
      } else {
        throw new Error("Failed to submit application. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      setSubmitError(error.message);
      alert(error.message); // Show error in an alert
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateAllFields = () => {
    const errors = {};
    // Add validation for all required fields
    if (!formData.firstName) errors.firstName = "First name is required";
    if (!formData.lastName) errors.lastName = "Last name is required";
    if (!formData.email) errors.email = "Email is required";
    if (!formData.phone) errors.phone = "Phone number is required";
    // Add more validations as needed
    return errors;
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-2xl font-semibold mb-6">
              Personal Information
            </h3>
            {renderInput("firstName", "First Name", "text", formData.firstName)}
            {renderInput("lastName", "Last Name", "text", formData.lastName)}
            {renderInput("email", "Email", "email", formData.email)}
            {renderInput("phone", "Phone", "tel", formData.phone)}
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold mb-6">Education</h3>
            {formData.education.map((edu, index) => (
              <div
                key={index}
                className="bg-gray-50 p-6 rounded-lg shadow-sm space-y-4"
              >
                {renderInput(
                  "school",
                  "School",
                  "text",
                  edu.school,
                  (e) => handleInputChange(e, index, "education"),
                  "education"
                )}
                {renderInput(
                  "degree",
                  "Degree",
                  "text",
                  edu.degree,
                  (e) => handleInputChange(e, index, "education"),
                  "education"
                )}
                {renderInput(
                  "fieldOfStudy",
                  "Field of Study",
                  "text",
                  edu.fieldOfStudy,
                  (e) => handleInputChange(e, index, "education"),
                  "education"
                )}
                {renderInput(
                  "graduationYear",
                  "Graduation Year",
                  "number",
                  edu.graduationYear,
                  (e) => handleInputChange(e, index, "education"),
                  "education"
                )}
                {formData.education.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveItem("education", index)}
                    className="text-red-600 hover:text-red-800 transition-colors"
                  >
                    Remove Education
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => handleAddItem("education")}
              className="text-primary hover:text-primary-dark transition-colors"
            >
              + Add Education
            </button>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold mb-6">Work Experience</h3>
            {formData.workExperience.map((exp, index) => (
              <div
                key={index}
                className="bg-gray-50 p-6 rounded-lg shadow-sm space-y-4"
              >
                {renderInput(
                  "company",
                  "Company",
                  "text",
                  exp.company,
                  (e) => handleInputChange(e, index, "workExperience"),
                  "workExperience"
                )}
                {renderInput(
                  "position",
                  "Position",
                  "text",
                  exp.position,
                  (e) => handleInputChange(e, index, "workExperience"),
                  "workExperience"
                )}
                {renderInput(
                  "startDate",
                  "Start Date",
                  "date",
                  exp.startDate,
                  (e) => handleInputChange(e, index, "workExperience"),
                  "workExperience"
                )}
                {renderInput(
                  "endDate",
                  "End Date",
                  "date",
                  exp.endDate,
                  (e) => handleInputChange(e, index, "workExperience"),
                  "workExperience"
                )}
                {renderTextarea(
                  "responsibilities",
                  "Responsibilities",
                  exp.responsibilities,
                  (e) => handleInputChange(e, index, "workExperience"),
                  "workExperience"
                )}
                {formData.workExperience.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveItem("workExperience", index)}
                    className="text-red-600 hover:text-red-800 transition-colors"
                  >
                    Remove Experience
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => handleAddItem("workExperience")}
              className="text-primary hover:text-primary-dark transition-colors"
            >
              + Add Work Experience
            </button>
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-2xl font-semibold mb-6">
              Skills and Documents
            </h3>
            {formData.skills.map((skill, index) => (
              <div key={index} className="flex items-center space-x-2">
                {renderInput(
                  `skill-${index}`,
                  `Skill ${index + 1}`,
                  "text",
                  skill,
                  (e) => handleInputChange(e, index, "skills"),
                  "skills"
                )}
                {formData.skills.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveItem("skills", index)}
                    className="text-red-600 hover:text-red-800 transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => handleAddItem("skills")}
              className="text-primary hover:text-primary-dark transition-colors"
            >
              + Add Skill
            </button>
            {renderFileInput("resume", "Resume")}
            {renderTextarea(
              "coverLetter",
              "Cover Letter",
              formData.coverLetter
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const renderInput = (
    name,
    label,
    type = "text",
    value,
    onChange,
    section
  ) => (
    <div>
      <label htmlFor={name} className="block font-medium mb-1">
        {label}
      </label>
      <input
        type={type}
        id={name}
        name={name}
        value={value}
        onChange={onChange || ((e) => handleInputChange(e))}
        className={`w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:border-primary ${
          fieldErrors[section] ? "border-red-500" : ""
        }`}
      />
      {fieldErrors[section] && (
        <p className="text-red-500 text-sm mt-1">{fieldErrors[section]}</p>
      )}
    </div>
  );

  const renderTextarea = (name, label, value, onChange, section) => (
    <div>
      <label htmlFor={name} className="block font-medium mb-1">
        {label}
      </label>
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={onChange || ((e) => handleInputChange(e))}
        className={`w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:border-primary h-32 ${
          fieldErrors[section] ? "border-red-500" : ""
        }`}
      ></textarea>
      {fieldErrors[section] && (
        <p className="text-red-500 text-sm mt-1">{fieldErrors[section]}</p>
      )}
    </div>
  );

  const renderFileInput = (name, label) => (
    <div>
      <label htmlFor={name} className="block font-medium mb-1">
        {label}
      </label>
      <input
        type="file"
        id={name}
        name={name}
        onChange={handleInputChange}
        className={`w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:border-primary ${
          fieldErrors[name] ? "border-red-500" : ""
        }`}
        accept=".pdf,.doc,.docx"
      />
      {formData[name] && (
        <p className="text-green-600 text-sm mt-1">
          File selected: {formData[name].name}
        </p>
      )}
      {fieldErrors[name] && (
        <p className="text-red-500 text-sm mt-1">{fieldErrors[name]}</p>
      )}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      {showSuccessAlert ? (
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold text-green-600">
            Application Submitted Successfully!
          </h2>
          <p className="text-lg">
            Thank you for applying. We will review your application and get back
            to you soon.
          </p>
          <Link
            to="/jobs"
            className="inline-block bg-accent text-white px-6 py-3 rounded-lg hover:bg-accent-dark transition-colors"
          >
            Back to Job Listings
          </Link>
        </div>
      ) : (
        <>
          {jobLoading ? (
            <div className="text-center">Loading job details...</div>
          ) : jobError ? (
            <div className="text-center text-red-600">{jobError}</div>
          ) : job ? (
            <>
              <div className="mb-8 p-6 bg-gray-50 rounded-lg">
                <h2 className="text-3xl font-bold mb-2">{job.title}</h2>
                <p className="text-xl text-gray-600 mb-4">{job.company}</p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  <div className="flex items-center">
                    <MapPin className="w-4 h-4 mr-1" />
                    {job.location}
                  </div>
                  <div className="flex items-center">
                    <Briefcase className="w-4 h-4 mr-1" />
                    {job.jobType}
                  </div>
                  <div className="flex items-center">
                    <DollarSign className="w-4 h-4 mr-1" />
                    {job.salary}
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    Posted {new Date(job.postedDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <h2 className="text-2xl font-bold mb-6">Application Form</h2>
              <div className="mb-8">
                <div className="flex justify-between items-center">
                  {[1, 2, 3, 4].map((step) => (
                    <div
                      key={step}
                      className={`w-1/4 text-center ${
                        currentStep >= step ? "text-primary" : "text-gray-400"
                      }`}
                    >
                      <div
                        className={`
                          w-10 h-10 mx-auto rounded-full flex items-center justify-center border-2
                          ${
                            currentStep >= step
                              ? "border-primary bg-primary-light"
                              : "border-gray-300"
                          }
                        `}
                      >
                        {step}
                      </div>
                      <div className="mt-2">
                        {step === 1 && "Personal"}
                        {step === 2 && "Education"}
                        {step === 3 && "Experience"}
                        {step === 4 && "Skills & Docs"}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="relative pt-1">
                  <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-primary-light">
                    <div
                      style={{ width: `${(currentStep / 4) * 100}%` }}
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary"
                    ></div>
                  </div>
                </div>
              </div>
              <form className="space-y-6">
                {renderStep()}
                <div className="flex justify-between mt-8">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handlePreviousStep}
                      className="flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5 mr-2" />
                      Previous
                    </button>
                  )}
                  {currentStep < 4 ? (
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors ml-auto"
                    >
                      Next
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </button>
                  ) : (
                    <>
                      <div className="w-full mb-4">
                        <div className="flex items-start mb-4">
                          <div className="flex items-center h-5">
                            <input
                              id="terms"
                              name="terms"
                              type="checkbox"
                              onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })}
                              className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-primary"
                              required
                            />
                          </div>
                          <label htmlFor="terms" className="ml-2 text-sm text-gray-600">
                            I agree to the{" "}
                            <Link to="/terms" target="_blank" className="text-primary hover:underline">
                              Terms of Service
                            </Link>{" "}
                            and{" "}
                            <Link to="/privacy" target="_blank" className="text-primary hover:underline">
                              Privacy Policy
                            </Link>
                          </label>
                        </div>
                        {fieldErrors.terms && (
                          <p className="text-red-500 text-sm mt-1">{fieldErrors.terms}</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleFinalSubmit}
                        disabled={isSubmitting || !formData.termsAccepted}
                        className="flex items-center px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent-dark transition-colors ml-auto disabled:bg-gray-400"
                      >
                        {isSubmitting ? "Submitting..." : "Submit Application"}
                      </button>
                    </>
                  )}
                </div>
              </form>
              {submitError && (
                <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-lg">
                  {submitError}
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-red-600">
              No job details found. Please go back to the job listings and
              select a job to apply for.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ApplicationForm;
